import { getConfig } from 'tw-oi-core'

import { ROUTE, UI } from '../config'
import { BRAND_SPECIFIC_TAG } from '../containers/BrandSpecificInformation/BrandSpecificInformation'
import {
  OWNERS_MANUAL_TITLE,
  containsArticles,
  containsDita,
  getArticlesWithTags,
  getPublicationsForVehicle,
  getUniqueTags,
  isRootFolder,
} from './utils'

function folderDirectlyContains(parent, resourceKey) {
  const contents = parent.get('contents')
  if (!contents) return false

  if (contents.keySeq && contents.keySeq().contains(resourceKey)) {
    return true
  }

  return contents.some
    ? contents.some(item => item && item.get('resourceKey') === resourceKey)
    : false
}

function isFolderAncestor(ancestor, descendant, foldersIndex) {
  let current = descendant
  const visited = new Set()

  while (current) {
    const key = current.get('resourceKey')
    if (!key || visited.has(key)) break
    visited.add(key)

    const parent = getImmediateParentFolder(current, foldersIndex)
    if (!parent) return false
    if (parent.get('resourceKey') === ancestor.get('resourceKey')) return true
    current = parent
  }

  return false
}

function getImmediateParentFolder(folder, foldersIndex) {
  const resourceKey = folder && folder.get('resourceKey')
  if (!resourceKey || !foldersIndex) return null

  const matches = foldersIndex.filter(parent => folderDirectlyContains(parent, resourceKey))
  if (!matches.size) return null
  if (matches.size === 1) return matches.first()

  return (
    matches.find(
      candidate =>
        !matches.some(other => other !== candidate && isFolderAncestor(candidate, other, foldersIndex))
    ) || matches.first()
  )
}

export function getBrowseRootBreadcrumb(baseRoute) {
  return { title: UI.ALL_CONTENT, link: `${baseRoute}${ROUTE.BROWSE}` }
}

export function isBrowseRootRoute(match, baseRoute) {
  return match.url === `${baseRoute}${ROUTE.BROWSE}`
}

function markLastItem(items) {
  if (!items.length) {
    return items
  }

  return items.map((item, index) =>
    index === items.length - 1 ? { title: item.title, link: '' } : item
  )
}

function folderLink(baseRoute, resourceKey) {
  return `${baseRoute}${ROUTE.BROWSE}${ROUTE.FOLDER}/${encodeURIComponent(resourceKey)}`
}

function ownersManualLink(baseRoute, rootFolderResourceKey) {
  return folderLink(baseRoute, rootFolderResourceKey)
}

export function getOwnersManualBreadcrumb(baseRoute, { rootFolderResourceKey, publications, articles } = {}) {
  if (publications) {
    const pubs = publications.toJS ? publications.toJS() : publications
    const vehiclePubs = getPublicationsForVehicle(pubs)

    if (containsDita(vehiclePubs) && rootFolderResourceKey) {
      return {
        title: UI.OWNERS_MANUAL,
        link: ownersManualLink(baseRoute, rootFolderResourceKey),
      }
    }

    const taggedArticles = getArticlesWithTags(vehiclePubs)
    const articleTags = taggedArticles.length ? getUniqueTags(taggedArticles) : []

    if (containsArticles(vehiclePubs) && articleTags.includes(OWNERS_MANUAL_TITLE)) {
      return {
        title: UI.OWNERS_MANUAL,
        link: `${baseRoute}${ROUTE.BROWSE}${ROUTE.TAGS}/${encodeURIComponent(OWNERS_MANUAL_TITLE)}`,
      }
    }
  } else if (articles) {
    const articleTags = getUniqueTags(articles)

    if (articleTags.includes(OWNERS_MANUAL_TITLE)) {
      return {
        title: UI.OWNERS_MANUAL,
        link: `${baseRoute}${ROUTE.BROWSE}${ROUTE.TAGS}/${encodeURIComponent(OWNERS_MANUAL_TITLE)}`,
      }
    }
  }

  if (rootFolderResourceKey) {
    return {
      title: UI.OWNERS_MANUAL,
      link: ownersManualLink(baseRoute, rootFolderResourceKey),
    }
  }

  return null
}

function findFolderByResourceKey(foldersIndex, resourceKey) {
  if (!foldersIndex || !resourceKey) return null

  return foldersIndex.get(resourceKey) || foldersIndex.find(folder => folder.get('resourceKey') === resourceKey) || null
}

function findFolderContainingResourceKey(foldersIndex, resourceKey) {
  if (!foldersIndex || !resourceKey) return null

  return foldersIndex.find(folder => {
    const contents = folder.get('contents')
    if (!contents) return false

    if (contents.keySeq && contents.keySeq().contains(resourceKey)) {
      return true
    }

    return contents.some
      ? contents.some(item => item && item.get('resourceKey') === resourceKey)
      : false
  }) || null
}

export function buildOwnersManualFolderBreadcrumbs(
  activeFolder,
  foldersIndex,
  baseRoute,
  rootFolderResourceKey
) {
  if (!activeFolder || !foldersIndex) return []

  const { BROWSE_ROOT_KEY } = getConfig()
  const breadcrumbs = []
  let folder = activeFolder

  while (folder) {
    const resourceKey = folder.get('resourceKey')

    if (isRootFolder(folder, rootFolderResourceKey)) {
      break
    }

    if (!resourceKey || resourceKey === BROWSE_ROOT_KEY) {
      break
    }

    breadcrumbs.unshift({
      title: folder.get('title'),
      link: folderLink(baseRoute, resourceKey),
    })

    folder = getImmediateParentFolder(folder, foldersIndex)
  }

  return breadcrumbs
}

function isAtOwnersManualRoot(activeFolder, rootFolderResourceKey) {
  return !!(
    activeFolder &&
    rootFolderResourceKey &&
    activeFolder.get('resourceKey') === rootFolderResourceKey
  )
}

function appendOwnersManualFolderBreadcrumbs(
  items,
  activeFolder,
  foldersIndex,
  baseRoute,
  rootFolderResourceKey,
  publications
) {
  const ownersManualCrumb = getOwnersManualBreadcrumb(baseRoute, { rootFolderResourceKey, publications })

  if (!ownersManualCrumb) {
    items.push(
      ...buildOwnersManualFolderBreadcrumbs(
        activeFolder,
        foldersIndex,
        baseRoute,
        rootFolderResourceKey
      )
    )
    return items
  }

  items.push(ownersManualCrumb)

  if (!isAtOwnersManualRoot(activeFolder, rootFolderResourceKey)) {
    items.push(
      ...buildOwnersManualFolderBreadcrumbs(
        activeFolder,
        foldersIndex,
        baseRoute,
        rootFolderResourceKey
      )
    )
  }

  return items
}

export function buildOwnersManualBreadcrumbTrail(
  activeFolder,
  foldersIndex,
  baseRoute,
  rootFolderResourceKey,
  publications
) {
  const items = []

  if (activeFolder) {
    appendOwnersManualFolderBreadcrumbs(
      items,
      activeFolder,
      foldersIndex,
      baseRoute,
      rootFolderResourceKey,
      publications
    )
  }

  return items
}

export function buildBrowseGuideBreadcrumbs(
  match,
  baseRoute,
  { foldersIndex, rootFolderResourceKey, topics, publications } = {}
) {
  if (isBrowseRootRoute(match, baseRoute)) {
    return []
  }

  const items = [getBrowseRootBreadcrumb(baseRoute)]
  const browseBase = `${baseRoute}${ROUTE.BROWSE}`

  if (match.params.folderId != null && match.params.folderId !== '') {
    const folderId = decodeURIComponent(match.params.folderId)
    const activeFolder = findFolderByResourceKey(foldersIndex, folderId)

    if (activeFolder) {
      appendOwnersManualFolderBreadcrumbs(
        items,
        activeFolder,
        foldersIndex,
        baseRoute,
        rootFolderResourceKey,
        publications
      )
    }

    return markLastItem(items)
  }

  if (match.params.topicId) {
    const topicId = decodeURIComponent(match.params.topicId)
    const parentFolder = findFolderContainingResourceKey(foldersIndex, topicId)

    if (parentFolder) {
      appendOwnersManualFolderBreadcrumbs(
        items,
        parentFolder,
        foldersIndex,
        baseRoute,
        rootFolderResourceKey,
        publications
      )
    }

    const topic = topics?.find(item => item.get('resourceKey') === topicId)
    if (topic) {
      items.push({
        title: topic.get('title'),
        link: `${browseBase}${ROUTE.TOPIC}/${encodeURIComponent(topicId)}`,
      })
    }

    return markLastItem(items)
  }

  if (match.url.includes(`${browseBase}${ROUTE.PRINTABLE_OWNERS_MANUAL}`)) {
    items.push({ title: UI.PRINTABLE_OWNERS_MANUAL, link: '' })
    return markLastItem(items)
  }

  if (match.url.includes(`${browseBase}${ROUTE.PDFS}`)) {
    items.push({ title: 'Component Manual', link: `${browseBase}${ROUTE.PDFS}` })
    if (match.params.pseudoFolderName) {
      items.push({
        title: decodeURIComponent(match.params.pseudoFolderName),
        link: `${browseBase}${ROUTE.PDFS}/${match.params.pseudoFolderName}`,
      })
    }
    return markLastItem(items)
  }

  if (match.url.includes(`${browseBase}${ROUTE.VIDEOS}`)) {
    items.push({ title: 'Videos', link: `${browseBase}${ROUTE.VIDEOS}` })
    if (match.params.pseudoFolderName) {
      items.push({
        title: decodeURIComponent(match.params.pseudoFolderName),
        link: `${browseBase}${ROUTE.VIDEOS}/${match.params.pseudoFolderName}`,
      })
    }
    return markLastItem(items)
  }

  if (match.url.includes(`${browseBase}${ROUTE.TAGS}`)) {
    const tag = match.params.tag ? decodeURIComponent(match.params.tag) : null
    const ownersManualCrumb = getOwnersManualBreadcrumb(baseRoute, { rootFolderResourceKey, publications })

    if (ownersManualCrumb && tag !== OWNERS_MANUAL_TITLE) {
      items.push(ownersManualCrumb)
    }

    if (tag) {
      items.push({
        title: tag,
        link: `${browseBase}${ROUTE.TAGS}/${match.params.tag}`,
      })
    }

    return markLastItem(items)
  }

  if (match.url.includes(`${browseBase}${ROUTE.ARTICLES}`) && !match.url.includes(`${ROUTE.CONTENT}/`)) {
    const ownersManualCrumb = getOwnersManualBreadcrumb(baseRoute, { rootFolderResourceKey, publications })

    if (ownersManualCrumb) {
      items.push(ownersManualCrumb)
    }

    if (match.params.tag) {
      items.push({
        title: decodeURIComponent(match.params.tag),
        link: `${browseBase}${ROUTE.ARTICLES}/${match.params.tag}`,
      })
    }
    return markLastItem(items)
  }

  if (match.url.includes(`${browseBase}${ROUTE.BRAND_SPECIFIC}`) && !match.url.includes(`${ROUTE.CONTENT}/`)) {
    items.push({ title: BRAND_SPECIFIC_TAG, link: '' })
    return markLastItem(items)
  }

  return items.length > 1 ? markLastItem(items) : []
}
